// Cricket Scoring (116) -- live runs/wickets entry + scoreboard. Over/total level.

import { useCallback, useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import type { CricketScoreResponse } from "@dugads/shared";
import * as api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { OfflineScoreBar } from "@/components/offline/offline-score-bar";
import { ChevronLeft, Undo2, Trophy, Loader2 } from "lucide-react";

const RUN_BUTTONS = [0, 1, 2, 3, 4, 6];

export default function CricketMatchScorePage() {
  const { id } = useParams<{ id: string }>();
  const [data, setData] = useState<CricketScoreResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    if (!id) return;
    api.getCricketScore(id).then(setData)
      .catch((e) => setError(e instanceof api.ApiError ? e.message : "Failed to load"))
      .finally(() => setLoading(false));
  }, [id]);
  useEffect(() => load(), [load]);

  async function record(body: { kind: "runs" | "wicket"; runs?: number }) {
    if (!id) return;
    setBusy(true);
    try { setData(await api.recordCricketEvent(id, body)); }
    catch (e) {
      if (e instanceof api.QueuedOfflineError) return; // saved offline; bar shows it
      setError(e instanceof api.ApiError ? e.message : "Failed");
    }
    finally { setBusy(false); }
  }
  async function undo() {
    if (!id || !data || data.events.length === 0) return;
    setBusy(true);
    try { setData(await api.undoCricketEvent(id, data.events[data.events.length - 1].id)); }
    catch (e) { if (!(e instanceof api.QueuedOfflineError)) setError(e instanceof api.ApiError ? e.message : "Failed"); }
    finally { setBusy(false); }
  }
  async function finalize() {
    if (!id || !confirm("Finalise this match?")) return;
    setBusy(true);
    try { setData(await api.finalizeCricket(id)); }
    catch (e) { if (!(e instanceof api.QueuedOfflineError)) setError(e instanceof api.ApiError ? e.message : "Failed"); }
    finally { setBusy(false); }
  }

  if (loading) return <div className="h-40 animate-pulse rounded-lg bg-muted" data-page="cricket-score" data-state="loading" />;
  if (error || !data) return <p className="text-sm text-destructive" data-page="cricket-score" data-error>{error ?? "Not found"}</p>;

  const r = data.result;
  const canRecord = r._actions.can_record;

  function SideRow({ side }: { side: "home" | "away" }) {
    const t = side === "home" ? r.home : r.away;
    const runs = side === "home" ? r.home_runs : r.away_runs;
    const wkts = side === "home" ? r.home_wickets : r.away_wickets;
    const overs = side === "home" ? r.home_overs : r.away_overs;
    const batting = (r.current_innings === 1 && side === "home") || (r.current_innings === 2 && side === "away");
    return (
      <div className="flex items-center justify-between gap-3 px-4 py-3" data-side={side}>
        <span className={`min-w-0 truncate text-sm ${r.winner_side === side ? "font-semibold" : "font-medium"}`}>
          {t.name ?? "TBD"}{batting && !r.final && <span className="ml-2 text-[10px] uppercase text-amber-700">batting</span>}
        </span>
        <span className="tabular-nums text-base font-semibold">
          {runs}/{wkts} <span className="text-xs font-normal text-muted-foreground">({overs})</span>
        </span>
      </div>
    );
  }

  return (
    <div className="mx-auto flex max-w-lg flex-col gap-4" data-page="cricket-score" data-state="ready">
      <Link to={`/events/${r.event_id}`} className="inline-flex w-fit items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
        <ChevronLeft className="h-3.5 w-3.5" /> Back to event
      </Link>
      <h1 className="text-xl font-semibold">{r.match_name}</h1>
      <p className="text-xs text-muted-foreground">{r.format} - {r.overs_per_innings} overs/innings</p>

      <OfflineScoreBar matchId={id} />

      <div className="rounded-lg border border-border bg-background" data-region="scoreboard">
        <SideRow side="home" />
        <div className="border-t border-border/60" />
        <SideRow side="away" />
        {r.target != null && !r.final && (
          <p className="px-4 pb-2 text-xs text-muted-foreground">Target: {r.target} (innings {r.current_innings})</p>
        )}
        {r.final && <p className="px-4 pb-2 text-sm font-semibold text-emerald-700">{r.result_summary}</p>}
      </div>

      {canRecord && (
        <>
          <div className="grid grid-cols-3 gap-2">
            {RUN_BUTTONS.map((n) => (
              <Button key={n} variant="outline" onClick={() => record({ kind: "runs", runs: n })} disabled={busy} className="h-14 text-base" data-action={`runs-${n}`}>
                {n === 0 ? "Dot" : `+${n}`}
              </Button>
            ))}
          </div>
          <Button onClick={() => record({ kind: "wicket" })} disabled={busy} variant="destructive" className="h-14 text-base" data-action="wicket">
            Wicket
          </Button>
          <div className="flex items-center justify-between">
            <Button variant="outline" size="sm" onClick={undo} disabled={busy || data.events.length === 0} data-action="undo">
              <Undo2 className="mr-1 h-4 w-4" /> Undo
            </Button>
            <Button variant="outline" size="sm" onClick={finalize} disabled={busy} data-action="finalize">
              {busy ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Trophy className="mr-1 h-4 w-4" />} Finalise
            </Button>
          </div>
        </>
      )}
    </div>
  );
}
